import React, { useState, useEffect } from "react";
import axios from "axios";

const TransferMoneyModal = ({
  show,
  onClose,
  onSubmit,
  sourceWallet,
  wallets,
  loading,
}) => {
  const [formData, setFormData] = useState({
    fromOwner: "",
    fromOwnerType: "",
    toOwner: "",
    toOwnerType: "",
    amount: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [sourceUserData, setSourceUserData] = useState(null);
  const [destUserData, setDestUserData] = useState(null);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchErrors, setFetchErrors] = useState({});

  // Reset form when modal opens/closes or sourceWallet changes
  useEffect(() => {
    if (show) {
      if (sourceWallet) {
        setFormData({
          fromOwner: sourceWallet.owner,
          fromOwnerType: sourceWallet.ownerType,
          toOwner: "",
          toOwnerType: "",
          amount: "",
          description: "",
        });
      } else {
        setFormData({
          fromOwner: "",
          fromOwnerType: "",
          toOwner: "",
          toOwnerType: "",
          amount: "",
          description: "",
        });
      }
      setErrors({});
    }
  }, [show, sourceWallet]);

  // Available wallets for source (if no source wallet selected)
  const availableSourceWallets = wallets.filter((w) => w.amount > 0);

  // Available wallets for destination (exclude the source wallet)
  const availableDestinationWallets = wallets.filter(
    (w) =>
      !(
        w.owner === formData.fromOwner && w.ownerType === formData.fromOwnerType
      ),
  );

  // Get selected source wallet details
  const selectedSourceWallet = wallets.find(
    (w) =>
      w.owner === formData.fromOwner && w.ownerType === formData.fromOwnerType,
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSourceWalletChange = (e) => {
    const [owner, ownerType] = e.target.value.split("|");
    setFormData((prev) => ({
      ...prev,
      fromOwner: owner,
      fromOwnerType: ownerType,
    }));
  };

  const handleDestinationWalletChange = (e) => {
    const [owner, ownerType] = e.target.value.split("|");
    setFormData((prev) => ({
      ...prev,
      toOwner: owner,
      toOwnerType: ownerType,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fromOwner) newErrors.fromOwner = "Source wallet is required";
    if (!formData.toOwner) newErrors.toOwner = "Destination wallet is required";
    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else {
      const amount = parseFloat(formData.amount);
      if (amount <= 0) {
        newErrors.amount = "Amount must be greater than 0";
      } else if (selectedSourceWallet && amount > selectedSourceWallet.amount) {
        newErrors.amount = "Amount exceeds available balance";
      }
    }

    if (
      formData.fromOwner === formData.toOwner &&
      formData.fromOwnerType === formData.toOwnerType
    ) {
      newErrors.toOwner = "Cannot transfer to the same wallet";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to fetch user data
  const fetchUserData = async (userId, setData, errorKey) => {
    if (!userId) return;

    setFetchingUsers(true);
    try {
      const response = await axios.get(`/api/users/${userId}`);
      setData(response.data);
      setFetchErrors((prev) => ({ ...prev, [errorKey]: null }));
    } catch (error) {
      console.error(`Error fetching user data for ${userId}:`, error);
      setFetchErrors((prev) => ({
        ...prev,
        [errorKey]: "Failed to load user data",
      }));
    } finally {
      setFetchingUsers(false);
    }
  };

  // Fetch source user data when the source wallet changes
  useEffect(() => {
    if (show && formData.fromOwner && formData.fromOwnerType === "User") {
      fetchUserData(formData.fromOwner, setSourceUserData, "source");
    } else {
      setSourceUserData(null);
    }
  }, [formData.fromOwner, formData.fromOwnerType, show]);

  // Fetch destination user data when the destination wallet changes
  useEffect(() => {
    if (show && formData.toOwner && formData.toOwnerType === "User") {
      fetchUserData(formData.toOwner, setDestUserData, "destination");
    } else {
      setDestUserData(null);
    }
  }, [formData.toOwner, formData.toOwnerType, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await onSubmit({
        fromOwner: formData.fromOwner,
        fromOwnerType: formData.fromOwnerType,
        toOwner: formData.toOwner,
        toOwnerType: formData.toOwnerType,
        amount: parseFloat(formData.amount),
        description: formData.description || "Wallet transfer",
      });
    } catch (error) {
      console.error("Transfer failed:", error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa fa-exchange-alt mr-2"></i>
              Transfer Money Between Wallets
            </h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                {/* Source Wallet */}
                <div className="col-md-6">
                  <div className="card border-primary mb-3">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">From Wallet</h6>
                    </div>
                    <div className="card-body">
                      {sourceWallet ? (
                        <div>
                          <p className="mb-1">
                            <strong>Owner:</strong>
                            {sourceWallet.ownerType === "User" &&
                            sourceUserData ? (
                              <span>
                                {sourceUserData.name ||
                                  (sourceUserData.firstName &&
                                  sourceUserData.lastName
                                    ? `${sourceUserData.firstName} ${sourceUserData.lastName}`
                                    : sourceWallet.owner)}
                                <small className="text-muted d-block">
                                  {sourceWallet.owner}
                                </small>
                              </span>
                            ) : (
                              <span>{sourceWallet.owner}</span>
                            )}
                            {fetchingUsers &&
                              sourceWallet.ownerType === "User" && (
                                <small>
                                  <i className="fa fa-spinner fa-spin ml-2"></i>
                                </small>
                              )}
                          </p>
                          <p className="mb-1">
                            <strong>Type:</strong>
                            <span
                              className={`badge ml-1 ${sourceWallet.ownerType === "User" ? "badge-primary" : "badge-info"}`}
                            >
                              {sourceWallet.ownerType}
                            </span>
                          </p>
                          <p className="mb-0">
                            <strong>Balance:</strong>
                            <span className="text-success ml-1">
                              {formatCurrency(sourceWallet.amount)}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <div className="form-group">
                          <label>Select Source Wallet</label>
                          <select
                            className={`form-control ${errors.fromOwner ? "is-invalid" : ""}`}
                            value={
                              formData.fromOwner && formData.fromOwnerType
                                ? `${formData.fromOwner}|${formData.fromOwnerType}`
                                : ""
                            }
                            onChange={handleSourceWalletChange}
                          >
                            <option value="">Choose source wallet...</option>
                            {availableSourceWallets.map((wallet) => {
                              const userData = wallets.find(
                                (w) => w._id === wallet._id,
                              )?.userData;
                              return (
                                <option
                                  key={wallet._id}
                                  value={`${wallet.owner}|${wallet.ownerType}`}
                                >
                                  {wallet.ownerType}:{" "}
                                  {wallet.ownerType === "User" && userData
                                    ? `${userData.firstName || ""} ${userData.lastName || ""} (${wallet.owner})`
                                    : wallet.owner}{" "}
                                  - {formatCurrency(wallet.amount)}
                                </option>
                              );
                            })}
                          </select>
                          {errors.fromOwner && (
                            <div className="invalid-feedback">
                              {errors.fromOwner}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Destination Wallet */}
                <div className="col-md-6">
                  <div className="card border-success mb-3">
                    <div className="card-header bg-success text-white">
                      <h6 className="mb-0">To Wallet</h6>
                    </div>
                    <div className="card-body">
                      <div className="form-group">
                        <label>Select Destination Wallet</label>
                        <select
                          className={`form-control ${errors.toOwner ? "is-invalid" : ""}`}
                          value={
                            formData.toOwner && formData.toOwnerType
                              ? `${formData.toOwner}|${formData.toOwnerType}`
                              : ""
                          }
                          onChange={handleDestinationWalletChange}
                        >
                          <option value="">Choose destination wallet...</option>
                          {availableDestinationWallets.map((wallet) => {
                            const userData = wallets.find(
                              (w) => w._id === wallet._id,
                            )?.userData;
                            return (
                              <option
                                key={wallet._id}
                                value={`${wallet.owner}|${wallet.ownerType}`}
                              >
                                {wallet.ownerType}:{" "}
                                {wallet.ownerType === "User" && userData
                                  ? `${userData.firstName || ""} ${userData.lastName || ""} (${wallet.owner})`
                                  : wallet.owner}{" "}
                                - {formatCurrency(wallet.amount)}
                              </option>
                            );
                          })}
                        </select>
                        {errors.toOwner && (
                          <div className="invalid-feedback">
                            {errors.toOwner}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transfer Details */}
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">Transfer Details</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Amount to Transfer *</label>
                        <div className="input-group">
                          <div className="input-group-prepend">
                            <span className="input-group-text">KES</span>
                          </div>
                          <input
                            type="number"
                            name="amount"
                            className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                            placeholder="0.00"
                            value={formData.amount}
                            onChange={handleInputChange}
                            min="0.01"
                            step="0.01"
                          />
                          {errors.amount && (
                            <div className="invalid-feedback">
                              {errors.amount}
                            </div>
                          )}
                        </div>
                        {selectedSourceWallet && (
                          <small className="text-muted">
                            Available:{" "}
                            {formatCurrency(selectedSourceWallet.amount)}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Description (Optional)</label>
                        <input
                          type="text"
                          name="description"
                          className="form-control"
                          placeholder="Transfer purpose..."
                          value={formData.description}
                          onChange={handleInputChange}
                          maxLength="100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transfer Summary */}
              {formData.amount && formData.fromOwner && formData.toOwner && (
                <div className="alert alert-info">
                  <h6>Transfer Summary:</h6>
                  <p className="mb-1">
                    <strong>From:</strong> {formData.fromOwnerType}{" "}
                    {formData.fromOwnerType === "User" && sourceUserData
                      ? `(${sourceUserData.firstName || ""} ${sourceUserData.lastName || ""} - ${formData.fromOwner})`
                      : `(${formData.fromOwner})`}
                  </p>
                  <p className="mb-1">
                    <strong>To:</strong> {formData.toOwnerType}{" "}
                    {formData.toOwnerType === "User" && destUserData
                      ? `(${destUserData.firstName || ""} ${destUserData.lastName || ""} - ${formData.toOwner})`
                      : `(${formData.toOwner})`}
                  </p>
                  <p className="mb-0">
                    <strong>Amount:</strong>{" "}
                    {formatCurrency(parseFloat(formData.amount) || 0)}
                  </p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={
                  loading ||
                  !formData.fromOwner ||
                  !formData.toOwner ||
                  !formData.amount
                }
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-exchange-alt mr-1"></i>
                    Transfer Money
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferMoneyModal;
